import type BaseLayer from 'ol/layer/Base'
import type Collection from 'ol/Collection'

import {
  BASE_LAYER_PROPERTY,
  RELIEF_LAYER_PROPERTY,
} from 'common/ol/arcgis-layers'

/**
 * What the globe paints where it has no imagery.
 *
 * Cesium's default is a navy blue meant for an ocean planet, which over
 * farmland reads as a rendering fault rather than as ground. A neutral mid grey
 * lets the DEM's own colour ramp over the field carry the meaning, and keeps
 * everything outside it legible as terrain.
 */
const BARE_TERRAIN_COLOR_CSS = '#8d8d8d'

/**
 * Showing and re-hiding the base map for the 3D surface selector.
 *
 * ol-cesium already mirrors OpenLayers layer visibility onto its Cesium imagery
 * layers, so hiding the base map needs no Cesium-side plumbing and no reaching
 * into the synchronizer's internals. Borrowing the 2D map's visibility state is
 * safe because the 2D canvas is not on screen while 3D is active — provided it
 * is put back exactly as it was, which is the only real complexity here and the
 * reason this is a separate, tested module. A visibility left at `false` is a
 * blank 2D map.
 */

/**
 * Paint the bare globe as ground rather than ocean, and restore the previous
 * colour afterwards.
 *
 * Returns a no-op when the scene is unavailable so callers can compose it
 * unconditionally.
 */
export const applyBareTerrainColor = (scene: any): (() => void) => {
  const Cesium = (globalThis as { Cesium?: any }).Cesium
  const globe = scene?.globe
  if (!globe || !Cesium?.Color?.fromCssColorString) return () => {}

  const previous = globe.baseColor
  globe.baseColor = Cesium.Color.fromCssColorString(BARE_TERRAIN_COLOR_CSS)
  scene.requestRender?.()

  let restored = false
  return () => {
    if (restored) return
    restored = true
    globe.baseColor = previous
    scene.requestRender?.()
  }
}

/** Base map layers, as tagged by the layer factories that create them. */
export const selectBaseLayers = (
  layers: Collection<BaseLayer>,
): BaseLayer[] => {
  const found: BaseLayer[] = []
  layers.forEach((layer) => {
    if (layer.get(BASE_LAYER_PROPERTY)) found.push(layer)
  })
  return found
}

/**
 * Switch the base map over to shaded relief, and return a function restoring
 * each layer to the visibility it had.
 *
 * A layer the user had already hidden stays hidden on restore: the caller only
 * undoes its own change, it does not assert what the base map "should" be.
 */
export const showReliefBaseLayer = (
  layers: Collection<BaseLayer>,
): (() => void) => {
  const previous = selectBaseLayers(layers).map(
    (layer) => [layer, layer.getVisible()] as const,
  )

  // Swap the satellite imagery for Esri's global shaded relief rather than
  // simply hiding it. A flat fill says nothing about the ground; the hillshade
  // is real terrain everywhere, and the harvest's own DEM colouring sits on top
  // of it where there is one.
  previous.forEach(([layer]) =>
    layer.setVisible(Boolean(layer.get(RELIEF_LAYER_PROPERTY))),
  )

  let restored = false
  return () => {
    // Guard against a double restore: React cleanups can run more than once
    // across a surface change plus an unmount, and the second run must not
    // resurrect a layer the user hid in between.
    if (restored) return
    restored = true
    previous.forEach(([layer, wasVisible]) => layer.setVisible(wasVisible))
  }
}
